import type {
  MessageEvent,
  MessageInstance,
  Project,
  SimulationRun,
  MessageRule,
  CoordinateConfig,
  TestSession,
} from '@k-ucap/shared';
import { SessionStatus } from '@k-ucap/shared';
import { createSimulationScheduler } from '@/domain/simulation/SimulationScheduler.js';
import { createMessageValidationService } from '@/domain/message/MessageValidationService.js';
import { createMessageDictionaryService } from '@/domain/message/MessageDictionaryService.js';
import { PreviewMessageEncoder } from '@/domain/message/PreviewMessageEncoder.js';
import { getGlobalVirtualMessageAdapter } from '@/infrastructure/adapters/VirtualMessageAdapter.js';
import { ReportGeneratorService } from '@/domain/report/ReportGeneratorService.js';
import type { Repositories } from '@/infrastructure/repositories/RepositoryInterfaces.js';

export interface DemoFlowResult {
  runId: string;
  reportId: string;
  sessionId: string;
  generatedCount: number;
}

const DEMO_VEHICLE_ID = 'UAV-001';

const defaultCoordinate: CoordinateConfig = {
  mode: 'CENTER_RADIUS',
  centerLat: 37.389,
  centerLon: 126.647,
  radiusKm: 10,
};

const createDemoMessageRules = (): MessageRule[] => [
  {
    id: 'demo-rule-101',
    messageType: 101,
    messageDefinitionId: 'stanag4586-101-inertial-states',
    enabled: true,
    sourceRole: 'AIR_VEHICLE',
    targetRole: 'CUCS',
    payloadTemplate: { vehicleId: DEMO_VEHICLE_ID, cucsId: 'CUCS-01' },
    fieldRules: {},
    generationMode: 'BULK',
    intervalMs: 0,
    count: 100,
    startDelayMs: 0,
    randomization: false,
    faultInjection: false,
    order: 0,
  },
  {
    id: 'demo-rule-104',
    messageType: 104,
    messageDefinitionId: 'stanag4586-104-vehicle-operating-states',
    enabled: true,
    sourceRole: 'AIR_VEHICLE',
    targetRole: 'CUCS',
    payloadTemplate: { vehicleId: DEMO_VEHICLE_ID, cucsId: 'CUCS-01' },
    fieldRules: {},
    generationMode: 'SINGLE',
    intervalMs: 0,
    count: 1,
    startDelayMs: 0,
    randomization: false,
    faultInjection: false,
    order: 1,
  },
  {
    id: 'demo-rule-1100',
    messageType: 1100,
    messageDefinitionId: 'stanag4586-1100-subsystem-status-alert',
    enabled: true,
    sourceRole: 'AIR_VEHICLE',
    targetRole: 'CUCS',
    payloadTemplate: {
      vehicleId: DEMO_VEHICLE_ID,
      cucsId: 'CUCS-01',
      severity: 'WARNING',
      message: 'Demo flight altitude warning',
    },
    fieldRules: {},
    generationMode: 'SINGLE',
    intervalMs: 0,
    count: 1,
    startDelayMs: 0,
    randomization: false,
    faultInjection: false,
    order: 2,
  },
];

const createDemoTest = (
  projectId: string,
  userId: string,
  environment: string
): {
  id: string;
  projectId: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  environment: string;
  vehicleIds: string[];
  messageRules: MessageRule[];
  locationConfig: { defaultCoordinate: CoordinateConfig };
  executionConfig: { recordMode: 'ALL' | 'SUMMARY' | 'SAMPLE' | 'NONE' };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
} => ({
  id: `demo-test-${Date.now()}`,
  projectId,
  name: 'Demo Flight Test',
  description: 'One-click demo test that generates sample messages and a report.',
  category: 'Demo',
  tags: ['demo', 'builtin'],
  environment,
  vehicleIds: [DEMO_VEHICLE_ID],
  messageRules: createDemoMessageRules(),
  locationConfig: { defaultCoordinate },
  executionConfig: { recordMode: 'ALL' },
  createdBy: userId,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const buildInitialRun = (
  test: {
    id: string;
    name: string;
    projectId: string;
    environment: string;
    executionConfig: SimulationRun['executionConfig'];
  },
  runId: string,
  userId: string
): SimulationRun => ({
  id: runId,
  simulationTestId: test.id,
  projectId: test.projectId,
  environment: test.environment,
  name: `${test.name} - ${new Date().toLocaleString()}`,
  status: 'running',
  startedAt: new Date().toISOString(),
  durationMs: 0,
  generatedCount: 0,
  sentCount: 0,
  droppedCount: 0,
  passCount: 0,
  warnCount: 0,
  failCount: 0,
  messageStats: {},
  vehicleStats: {},
  executionConfig: test.executionConfig,
  createdBy: userId,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const createMessageEvent = (
  message: MessageInstance,
  projectId: string,
  runId: string,
  severity: string,
  sessionId?: string
): MessageEvent => ({
  id: message.id,
  projectId,
  runId,
  messageType: message.messageType,
  standard: message.standard,
  name: message.name,
  source: message.source,
  target: message.target,
  sourceRole: message.source,
  targetRole: message.target,
  direction: message.direction,
  timestamp: message.timestamp,
  payload: message.fields,
  fields: message.fields,
  sequenceNumber: message.sequenceNumber ?? 0,
  validation: severity,
  validationStatus: severity,
  environment: message.environment ?? 'development',
  createdAt: new Date().toISOString(),
  createdBy: 'demo-flow',
  metadata: sessionId ? { sessionId } : undefined,
});

export class DemoFlowService {
  constructor(private readonly repositories: Repositories) {}

  async run(project: Project, userId: string): Promise<DemoFlowResult> {
    const test = createDemoTest(project.id, userId, project.environment);
    await this.repositories.simulationTestRepository.save(test);

    const runId = `run-${Date.now()}`;
    const run = buildInitialRun(test, runId, userId);

    // Prompt 14 Phase 3: Demo 전체 Flow를 하나의 TestSession으로 연결
    const now = new Date().toISOString();
    const session: TestSession = {
      id: `session-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      projectId: project.id,
      simulationTestId: test.id,
      environment: project.environment,
      startedAt: now,
      status: SessionStatus.RUNNING,
      messageCount: 0,
      passCount: 0,
      warnCount: 0,
      failCount: 0,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };
    await this.repositories.sessionRepository.save(session);
    const sessionId = session.id;

    const runWithSession: SimulationRun = {
      ...run,
      metadata: { ...run.metadata, sessionId },
    };
    await this.repositories.simulationRunRepository.save(runWithSession);

    const dictionaryService = createMessageDictionaryService();
    const validationService = createMessageValidationService();
    const encoder = new PreviewMessageEncoder();
    const adapter = getGlobalVirtualMessageAdapter();

    const events: MessageEvent[] = [];
    const vehicleStats: SimulationRun['vehicleStats'] = {};
    let generatedCount = 0;
    let passCount = 0;
    let warnCount = 0;
    let failCount = 0;

    await new Promise<void>((resolve, reject) => {
      const scheduler = createSimulationScheduler({
        test,
        seed: `demo-${runId}`,
        tickMs: 0,
        chunkSize: 50,
        callbacks: {
          onMessage: (ruleId, message) => {
            const rule = test.messageRules.find((r) => r.id === ruleId);
            if (!rule) return;
            const definition = dictionaryService.getMessageByType(rule.messageType);
            if (!definition) return;

            const validation = validationService.validate(definition, message.fields);
            const severity = validation.severity === 'INFO' ? 'PASS' : validation.severity;

            const enriched: MessageInstance = {
              ...message,
              projectId: project.id,
              environment: project.environment,
              runId,
            };
            const event = createMessageEvent(enriched, project.id, runId, severity, sessionId);
            events.push(event);

            generatedCount++;
            if (severity === 'PASS') passCount++;
            if (severity === 'WARN') warnCount++;
            if (severity === 'FAIL') failCount++;

            const coords = enriched.fields.coordinates as
              { latitude: number; longitude: number; altitudeM: number } | undefined;
            const lat = coords?.latitude ?? (enriched.fields.latitude as number | undefined);
            const lon = coords?.longitude ?? (enriched.fields.longitude as number | undefined);
            const alt = coords?.altitudeM ?? (enriched.fields.altitudeM as number | undefined);
            const existing = vehicleStats[DEMO_VEHICLE_ID] ?? { messageCount: 0 };
            vehicleStats[DEMO_VEHICLE_ID] = {
              messageCount: existing.messageCount + 1,
              lastCoordinates:
                lat !== undefined && lon !== undefined
                  ? { latitude: lat, longitude: lon, altitudeM: alt ?? 0 }
                  : existing.lastCoordinates,
            };

            try {
              const rawBytes = encoder.encode(enriched);
              adapter.publish({ ...enriched, rawBytes: Array.from(rawBytes) }).catch(() => {
                adapter.publish(enriched).catch(() => undefined);
              });
            } catch {
              adapter.publish(enriched).catch(() => undefined);
            }

            this.repositories.messageEventRepository.save(event).catch(() => undefined);
          },
          onRuleStatus: () => {},
          onElapsed: () => {},
          onStatusChange: (status) => {
            if (status === 'COMPLETED' || status === 'STOPPED' || status === 'ERROR') {
              // scheduler.stop()을 여기서 호출하면 stop() → onStatusChange('STOPPED') → 재진입으로 무한 재귀가 발생한다.
              // completeRun()은 이미 timer를 정리하므로 resolve만 수행한다.
              resolve();
            }
          },
          onError: (error) => {
            scheduler.stop();
            reject(new Error(error));
          },
        },
      });
      scheduler.start();
    });

    const messageStats: SimulationRun['messageStats'] = {};
    events.forEach((event) => {
      const stats = messageStats[event.messageType] ?? { generated: 0, sent: 0, pass: 0, warn: 0, fail: 0 };
      stats.generated++;
      stats.sent++;
      if (event.validationStatus === 'PASS') stats.pass++;
      if (event.validationStatus === 'WARN') stats.warn++;
      if (event.validationStatus === 'FAIL') stats.fail++;
      messageStats[event.messageType] = stats;
    });

    const finalRun: SimulationRun = {
      ...runWithSession,
      status: 'completed',
      endedAt: new Date().toISOString(),
      durationMs: events.length * 50,
      generatedCount,
      sentCount: generatedCount,
      droppedCount: 0,
      passCount,
      warnCount,
      failCount,
      messageStats,
      vehicleStats,
      updatedAt: new Date().toISOString(),
    };
    await this.repositories.simulationRunRepository.save(finalRun);

    // Session 종료 및 통계 반영
    const completedSession: TestSession = {
      ...session,
      status: SessionStatus.COMPLETED,
      endedAt: new Date().toISOString(),
      messageCount: generatedCount,
      passCount,
      warnCount,
      failCount,
      updatedAt: new Date().toISOString(),
    };
    await this.repositories.sessionRepository.save(completedSession);

    const definitions = dictionaryService.getAllMessages();
    const report = ReportGeneratorService.generate({
      run: finalRun,
      events,
      definitions,
      title: `${project.name} Demo Report`,
    });
    await this.repositories.reportRepository.save(report);

    return { runId: finalRun.id, reportId: report.id, sessionId, generatedCount };
  }
}

export const createDemoFlowService = (repositories: Repositories): DemoFlowService => new DemoFlowService(repositories);
